import {
  Building2,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  carriersApiService,
  useAddHandleMutation,
  useCreateCarrierMutation,
  useCreateDivisionMutation,
  useLinkDivisionHandleMutation,
} from '@/services/api/carriersApiService';

import type { HandleType, HealthCarrierType } from './types';
import { HEALTH_CARRIER_TYPE_OPTIONS } from './types';

// US State abbreviations for dropdown
const US_STATES = [
  { value: '', label: 'Select state...' },
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];

interface CreateCarrierModalProps {
  open: boolean;
  onClose: () => void;
  /** Pre-select health insurance mode for lien carrier creation */
  isHealthCarrier?: boolean;
}

interface DraftDivision {
  id: string;
  name: string;
  isDefault: boolean;
}

interface DraftHandle {
  id: string;
  name: string;
  handleType: HandleType;
  value: string;
  divisionId: string;
  description: string;
  // Address fields for MAILING_ADDRESS type
  addressLine1?: string;
  addressLine2?: string;
  handleCity?: string;
  handleState?: string;
  handleZip?: string;
}

const HANDLE_TYPE_OPTIONS: { value: HandleType; label: string }[] = [
  { value: 'PHONE', label: 'Phone' },
  { value: 'FAX', label: 'Fax' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'WEBSITE', label: 'Website' },
  { value: 'MAILING_ADDRESS', label: 'Address' },
];

let draftIdCounter = 0;
const generateDraftId = () => {
  draftIdCounter += 1;
  return `draft-${draftIdCounter}`;
};

export const CreateCarrierModal = ({
  open,
  onClose,
  isHealthCarrier = false,
}: CreateCarrierModalProps) => {
  const [carrierName, setCarrierName] = useState('');
  const [divisions, setDivisions] = useState<DraftDivision[]>([
    { id: generateDraftId(), name: 'Main', isDefault: true },
  ]);
  const [handles, setHandles] = useState<DraftHandle[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddress, setShowAddress] = useState(false);
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [healthCarrierType, setHealthCarrierType] =
    useState<HealthCarrierType | null>(null);

  // API mutations
  const [createCarrier] = useCreateCarrierMutation();
  const [createDivision] = useCreateDivisionMutation();
  const [createHandleApi] = useAddHandleMutation();
  const [linkHandle] = useLinkDivisionHandleMutation();
  const [fetchDivisions] =
    carriersApiService.endpoints.listDivisions.useLazyQuery();

  const resetForm = () => {
    setCarrierName('');
    setDivisions([{ id: generateDraftId(), name: 'Main', isDefault: true }]);
    setHandles([]);
    setShowAddress(false);
    setAddressLine1('');
    setAddressLine2('');
    setCity('');
    setState('');
    setZip('');
    setHealthCarrierType(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Division management
  const handleAddDivision = () => {
    setDivisions([
      ...divisions,
      { id: generateDraftId(), name: '', isDefault: false },
    ]);
  };

  const updateDivision = (id: string, name: string) => {
    setDivisions(divisions.map((d) => (d.id === id ? { ...d, name } : d)));
  };

  const removeDivision = (id: string) => {
    if (divisions.length <= 1) {
      toast.error('At least one division is required');
      return;
    }
    const firstDivisionId = divisions.find((d) => d.id !== id)?.id || '';
    setHandles(
      handles.map((h) =>
        h.divisionId === id ? { ...h, divisionId: firstDivisionId } : h,
      ),
    );
    const wasDefault = divisions.find((d) => d.id === id)?.isDefault;
    const newDivisions = divisions.filter((d) => d.id !== id);
    if (wasDefault && newDivisions.length > 0) {
      newDivisions[0].isDefault = true;
    }
    setDivisions(newDivisions);
  };

  const setDefaultDivision = (id: string) => {
    setDivisions(divisions.map((d) => ({ ...d, isDefault: d.id === id })));
  };

  // Handle management
  const handleAddHandle = () => {
    const defaultDivision = divisions.find((d) => d.isDefault) || divisions[0];
    setHandles([
      ...handles,
      {
        id: generateDraftId(),
        name: '',
        handleType: 'PHONE',
        value: '',
        divisionId: defaultDivision?.id || '',
        description: '',
      },
    ]);
  };

  const updateHandle = (id: string, updates: Partial<DraftHandle>) => {
    setHandles(handles.map((h) => (h.id === id ? { ...h, ...updates } : h)));
  };

  const removeHandle = (id: string) => {
    setHandles(handles.filter((h) => h.id !== id));
  };

  // Submit handler
  const handleSubmit = async () => {
    if (!carrierName.trim()) {
      toast.error('Please enter a carrier name');
      return;
    }

    if (divisions.some((d) => !d.name.trim())) {
      toast.error('All divisions must have names');
      return;
    }

    setIsSubmitting(true);

    try {
      // 1. Create the carrier (backend auto-creates a "Main" division)
      const carrier = await createCarrier({
        name: carrierName.trim(),
        addressLine1: addressLine1.trim() || undefined,
        addressLine2: addressLine2.trim() || undefined,
        city: city.trim() || undefined,
        state: state || undefined,
        zip: zip.trim() || undefined,
        healthCarrierType: healthCarrierType || undefined,
      }).unwrap();

      // 2. Fetch existing divisions (includes auto-created "Main" division)
      const existingDivisionsResult = await fetchDivisions({
        carrierId: carrier.id,
      });
      const existingDivisions = existingDivisionsResult.data || [];

      // 3. Build mapping from draft IDs to real IDs, reusing existing divisions where possible
      const divisionIdMap = new Map<string, string>();

      // Create divisions sequentially, skipping ones that already exist
      await divisions.reduce(async (prevPromise, division) => {
        await prevPromise;
        const trimmedName = division.name.trim();

        // Check if a division with this name already exists (case-insensitive)
        const existingDiv = existingDivisions.find(
          (d) => d.divisionName.toLowerCase() === trimmedName.toLowerCase(),
        );

        if (existingDiv) {
          // Reuse the existing division
          divisionIdMap.set(division.id, existingDiv.id);
        } else {
          // Create new division
          const createdDivision = await createDivision({
            carrierId: carrier.id,
            divisionName: trimmedName,
          }).unwrap();
          divisionIdMap.set(division.id, createdDivision.id);
        }
      }, Promise.resolve());

      // 4. Create all handles and link them to divisions
      // Filter valid handles: either has value (for simple types) or has address fields (for MAILING_ADDRESS)
      const validHandles = handles.filter((h) => {
        if (h.handleType === 'MAILING_ADDRESS') {
          return h.addressLine1?.trim();
        }
        return h.value.trim();
      });
      await validHandles.reduce(async (prevPromise, handle) => {
        await prevPromise;
        const createdHandle = await createHandleApi({
          carrierId: carrier.id,
          handleType: handle.handleType,
          value: handle.value?.trim() || undefined,
          name: handle.name.trim() || undefined,
          description: handle.description?.trim() || undefined,
          // Address fields for MAILING_ADDRESS
          addressLine1: handle.addressLine1?.trim() || undefined,
          addressLine2: handle.addressLine2?.trim() || undefined,
          city: handle.handleCity?.trim() || undefined,
          state: handle.handleState || undefined,
          zip: handle.handleZip?.trim() || undefined,
        }).unwrap();

        // Link handle to its division
        const realDivisionId = divisionIdMap.get(handle.divisionId);
        if (realDivisionId) {
          await linkHandle({
            carrierId: carrier.id,
            divisionId: realDivisionId,
            handleId: createdHandle.id,
          }).unwrap();
        }
      }, Promise.resolve());

      toast.success(`${carrierName} created successfully`);
      handleClose();
    } catch (error) {
      console.error('Failed to create carrier:', error);
      toast.error('Failed to create carrier. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            {isHealthCarrier
              ? 'Add Health Insurance Carrier'
              : 'Create New Carrier'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 py-4 pr-2">
          {/* Carrier Name */}
          <div className="space-y-2">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label
              htmlFor="carrier-name-input"
              className="text-sm font-medium text-gray-700"
            >
              Carrier Name <span className="text-error">*</span>
            </label>
            <input
              id="carrier-name-input"
              type="text"
              placeholder={
                isHealthCarrier
                  ? 'e.g., Blue Cross Blue Shield'
                  : 'e.g., State Farm Insurance'
              }
              value={carrierName}
              onChange={(e) => setCarrierName(e.target.value)}
              className="w-full px-4 py-2.5 text-base border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
            />
          </div>

          {/* Health Carrier Type - shown for health insurance carriers */}
          {isHealthCarrier && (
            <div className="space-y-2">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label
                htmlFor="health-carrier-type"
                className="text-sm font-medium text-gray-700"
              >
                Carrier Type
              </label>
              <select
                id="health-carrier-type"
                value={healthCarrierType || ''}
                onChange={(e) =>
                  setHealthCarrierType(
                    (e.target.value as HealthCarrierType) || null,
                  )
                }
                className="w-full px-4 py-2.5 text-base border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
              >
                <option value="">Select type (optional)</option>
                {HEALTH_CARRIER_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-400">
                Helps identify lien rights and correspondence templates
              </p>
            </div>
          )}

          {/* Corporate Address Section */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setShowAddress(!showAddress)}
              className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              {showAddress ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
              Corporate Address
              <span className="text-xs text-gray-400 font-normal">
                (optional)
              </span>
            </button>

            {showAddress && (
              <div className="space-y-3 pl-6 border-l-2 border-gray-100">
                <div>
                  <input
                    type="text"
                    placeholder="Address Line 1"
                    value={addressLine1}
                    onChange={(e) => setAddressLine1(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    placeholder="Address Line 2 (Suite, Unit, etc.)"
                    value={addressLine2}
                    onChange={(e) => setAddressLine2(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  />
                </div>
                <div className="flex gap-3">
                  <input
                    type="text"
                    placeholder="City"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  />
                  <select
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="w-32 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
                  >
                    {US_STATES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.value || s.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="ZIP"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    className="w-24 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Divisions Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                Divisions
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleAddDivision}
                className="text-primary hover:text-primary-dark"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Division
              </Button>
            </div>
            <div className="space-y-2">
              {divisions.map((division, index) => (
                <div
                  key={division.id}
                  className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg"
                >
                  <input
                    type="text"
                    placeholder="Division name..."
                    value={division.name}
                    onChange={(e) =>
                      updateDivision(division.id, e.target.value)
                    }
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus={
                      index === divisions.length - 1 && division.name === ''
                    }
                    className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
                  />
                  <button
                    type="button"
                    onClick={() => setDefaultDivision(division.id)}
                    className={`px-2 py-1 text-xs rounded transition-colors ${
                      division.isDefault
                        ? 'bg-primary text-white'
                        : 'bg-white border border-gray-200 text-gray-500 hover:border-primary hover:text-primary'
                    }`}
                  >
                    Default
                  </button>
                  {divisions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeDivision(division.id)}
                      className="p-1.5 text-gray-400 hover:text-error hover:bg-error/5 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400">
              Divisions help organize contact info by region or department
              (e.g., &ldquo;Florida Claims&rdquo;, &ldquo;National
              Office&rdquo;)
            </p>
          </div>

          {/* Contact Handles Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                Contact Information
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleAddHandle}
                className="text-primary hover:text-primary-dark"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Contact
              </Button>
            </div>

            {handles.length === 0 ? (
              <div className="text-center py-6 border-2 border-dashed border-gray-200 rounded-lg">
                <p className="text-sm text-gray-400 mb-2">
                  No contacts added yet
                </p>
                <Button variant="outline" size="sm" onClick={handleAddHandle}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add your first contact
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {handles.map((handle) => (
                  <div
                    key={handle.id}
                    className="p-4 bg-gray-50 rounded-lg space-y-3"
                  >
                    {/* Type Row */}
                    <div className="flex gap-2 items-center">
                      <div className="flex-shrink-0">
                        <select
                          value={handle.handleType}
                          onChange={(e) =>
                            updateHandle(handle.id, {
                              handleType: e.target.value as HandleType,
                              // Clear fields when switching types
                              value: '',
                              addressLine1: '',
                              addressLine2: '',
                              handleCity: '',
                              handleState: '',
                              handleZip: '',
                            })
                          }
                          className="h-10 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
                        >
                          {HANDLE_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      {handle.handleType !== 'MAILING_ADDRESS' && (
                        <input
                          type="text"
                          placeholder={
                            handle.handleType === 'EMAIL'
                              ? 'claims@example.com'
                              : handle.handleType === 'WEBSITE'
                                ? 'https://example.com'
                                : '(555) 123-4567'
                          }
                          value={handle.value}
                          onChange={(e) =>
                            updateHandle(handle.id, { value: e.target.value })
                          }
                          className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => removeHandle(handle.id)}
                        className="p-2 text-gray-400 hover:text-error hover:bg-error/5 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Address Fields for MAILING_ADDRESS */}
                    {handle.handleType === 'MAILING_ADDRESS' && (
                      <div className="space-y-2">
                        <input
                          type="text"
                          placeholder="Address Line 1"
                          value={handle.addressLine1 || ''}
                          onChange={(e) =>
                            updateHandle(handle.id, {
                              addressLine1: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
                        />
                        <input
                          type="text"
                          placeholder="Address Line 2 (optional)"
                          value={handle.addressLine2 || ''}
                          onChange={(e) =>
                            updateHandle(handle.id, {
                              addressLine2: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
                        />
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="City"
                            value={handle.handleCity || ''}
                            onChange={(e) =>
                              updateHandle(handle.id, {
                                handleCity: e.target.value,
                              })
                            }
                            className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
                          />
                          <select
                            value={handle.handleState || ''}
                            onChange={(e) =>
                              updateHandle(handle.id, {
                                handleState: e.target.value,
                              })
                            }
                            className="w-24 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
                          >
                            {US_STATES.map((s) => (
                              <option key={s.value} value={s.value}>
                                {s.value || s.label}
                              </option>
                            ))}
                          </select>
                          <input
                            type="text"
                            placeholder="ZIP"
                            value={handle.handleZip || ''}
                            onChange={(e) =>
                              updateHandle(handle.id, {
                                handleZip: e.target.value,
                              })
                            }
                            className="w-24 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
                          />
                        </div>
                      </div>
                    )}

                    {/* Label & Division Row */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Label (e.g., Florida BI Claims)"
                        value={handle.name}
                        onChange={(e) =>
                          updateHandle(handle.id, { name: e.target.value })
                        }
                        className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
                      />
                      <select
                        value={handle.divisionId}
                        onChange={(e) =>
                          updateHandle(handle.id, {
                            divisionId: e.target.value,
                          })
                        }
                        className="w-40 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
                      >
                        {divisions.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.name || 'Unnamed'}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button variant="ghost" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Carrier'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
